import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../app.environments';
import { getAuthToken, setAuthToken } from '../../utils/auth.utils';
import { PermissionService } from '../../permissions/permission.service';

export interface LoginResponse {
  id: number;
  uuid: string;
  status: string;
  name: string;
  accessToken: string;
  role: number;
  warehouse_id: number;
  store: number;
  client_id: number;
  warehouse?: any; // Optional warehouse/store details from auth response
  countersession?: any; // Optional counter session details
  industry?: number; // Added to support multi-domain routing
  is_expired?: boolean; // Indicates if trial is expired
}

export interface userProfile {
  success: true;
  data: {
    name: string;
    email: string;
    status: Boolean;
    createdAt: string;
    role: {
      name: string;
    };
  };
}

import { TenantService } from '../../tenant/tenant.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly USER_DATA_KEY = 'user_data';
  private readonly API_URL = `${environment.apiURL}/auth`;
  private readonly userUrl = `${environment.apiURL}/hr/users/`;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private permissionService: PermissionService,
    private tenantService: TenantService
  ) {
    const isAuthenticated = this.hasValidToken();
    this.isAuthenticatedSubject.next(isAuthenticated);

    // If user is already authenticated (page refresh), load their permissions
    if (isAuthenticated) {
      this.loadPermissionsOnInit();
    }
  }

  /**
   * Load permissions on app initialization (page refresh)
   */
  private loadPermissionsOnInit(): void {
    const userData = this.getUserData();

    if (userData && userData.role !== undefined && userData.role !== null) {
      this.permissionService.loadPermissionsForRole(userData.role).subscribe({
        next: () => {
          // Permissions loaded successfully on page refresh
        },
        error: (error: any) => {
          console.error('Failed to load permissions on init:', error);
        }
      });
    }
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.API_URL, { username, password }).pipe(
      tap((response) => {
        if (response && response.status === 'Success' && response.accessToken) {
          setAuthToken(response.accessToken);
          this.setUserData(response);
          this.isAuthenticatedSubject.next(true);

          // Store warehouse/store details if available (optional - won't break if not present)
          if (response.warehouse) {
            localStorage.setItem('storename', JSON.stringify(response.warehouse));
            // //console.log('[AuthService] Warehouse details stored:', response.warehouse);
          }

          // Store counter session if available (optional)
          if (response.countersession) {
            localStorage.setItem('countersession', JSON.stringify(response.countersession));
            // //console.log('[AuthService] Counter session stored');
          }

          // TEMPORARY: Force is_expired to true for testing purposes
          // response.is_expired = true;

          localStorage.setItem('user', JSON.stringify(response));
          const targetRoute = response.is_expired ? '/trial-expired' : '/';
          // Load permissions based on user role
          if (response.role !== undefined && response.role !== null) {
            this.permissionService.loadPermissionsForRole(response.role).subscribe({
              next: () => {
                this.tenantService.loadCachedConfig();
                this.router.navigate([targetRoute]);
              },
              error: (error: any) => {
                console.error('Failed to load permissions:', error);
                // Still navigate to dashboard even if permissions fail
                this.tenantService.loadCachedConfig();
                this.router.navigate([targetRoute]);
              }
            });
          } else {
            // //console.log('No role found in response, navigating to dashboard without permissions');
            this.tenantService.loadCachedConfig();
            this.router.navigate([targetRoute]);
          }
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem('user');
    localStorage.removeItem('storename');
    localStorage.removeItem('countersession');
    this.tenantService.clearConfig();
    this.permissionService.clearPermissions();
    this.isAuthenticatedSubject.next(false);
    this.router.navigate(['/login']);
  }

  sendOTP(username: string): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/sendOtp`, { username });
  }

  resetPassword(payload: { username: string; otp: string; newPassword: string }): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/resetPassword`, payload);
  }

  sendTrialExpiredAction(payload: any): Observable<any> {
    // Assuming a backend endpoint for handling trial expired actions
    return this.http.post<any>(`${this.API_URL}/trial-expired-action`, payload);
  }


  verifyOTP(username: string, otp: string): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/verifyOtp`, {
      username,
      otp,
    });
  }

  getDumpresult(client_id: number): Observable<any> {
    return this.http.get<any>(`${this.userUrl}getproductdumpresult/${client_id}`);
  }

  getUserProfile(uuid: string): Observable<userProfile> {
    return this.http.get<userProfile>(`${this.userUrl}userprofile/${uuid}`);
  }

  setUserData(userData: any): void {
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
  }

  getUserData(): any {
    const userData = localStorage.getItem(this.USER_DATA_KEY);
    const parsedData = userData ? JSON.parse(userData) : null;
    if (parsedData) {
      // parsedData.is_expired = true; // TEMPORARY: Force true for testing
    }
    return parsedData;
  }

  hasValidToken(): boolean {
    const token = getAuthToken();
    return !!token;
  }

  isLoggedIn(): boolean {
    return this.hasValidToken();
  }

  /**
   * Get current user's role ID
   */
  getCurrentUserRole(): number | null {
    const userData = this.getUserData();
    return userData?.role || null;
  }

  /**
   * Refresh user permissions
   */
  refreshPermissions(): void {
    const roleId = this.getCurrentUserRole();
    if (roleId) {
      this.permissionService.loadPermissionsForRole(roleId).subscribe({
        next: (permissions: any) => {
          // //console.log('Permissions refreshed successfully:', permissions);
        },
        error: (error: any) => {
          console.error('Failed to refresh permissions:', error);
        }
      });
    }
  }
}
